import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser, isAuthenticated, removeCurrentUser, setCurrentUser, getUserProfile } from '../services/authService';
import apiClient from '../services/apiClient';

// Create the auth context
const AuthContext = createContext();

// Session timeout constants — defined at module level so they are stable references
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
const INACTIVITY_CHECK_INTERVAL = 60 * 1000; // Check every 1 minute

// Auth provider component
export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trialStatus, setTrialStatus] = useState(null);
  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);
  const [showPaymentMethodRequiredModal, setShowPaymentMethodRequiredModal] = useState(false);
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [impersonating, setImpersonating] = useState(null); // { actualUser, impersonatedUser }
  
  // Track user activity
  const updateLastActivity = () => {
    localStorage.setItem('lastActivity', Date.now().toString());
  };
  
  // Handle session expiration
  const handleSessionExpiration = useCallback(() => {
    setShowSessionExpiredModal(true);
    // Clean up after showing modal
    setTimeout(() => {
      removeCurrentUser();
      setUser(null);
      setTrialStatus(null);
      setShowTrialExpiredModal(false);
      setShowPaymentMethodRequiredModal(false);
      localStorage.removeItem('lastActivity');
      navigate('/');
    }, 100);
  }, [navigate]);

  // Check if session has expired due to inactivity
  const checkSessionExpiration = useCallback(() => {
    if (!isAuthenticated()) {
      return false;
    }

    const lastActivity = localStorage.getItem('lastActivity');
    if (!lastActivity) {
      updateLastActivity();
      return false;
    }

    const timeSinceLastActivity = Date.now() - parseInt(lastActivity);

    if (timeSinceLastActivity > SESSION_TIMEOUT) {
      console.log('⏰ Session expired due to inactivity');
      handleSessionExpiration();
      return true;
    }

    return false;
  }, [handleSessionExpiration]);
  
  // Set up activity listeners
  useEffect(() => {
    if (!isAuthenticated() || !user) {
      return;
    }

    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    // Throttle writes to localStorage — at most once every 10 seconds for high-frequency events
    let throttleTimer = null;
    const handleActivity = () => {
      if (!throttleTimer) {
        updateLastActivity();
        throttleTimer = setTimeout(() => { throttleTimer = null; }, 10000);
      }
    };

    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, true);
    });

    // Set up periodic check for session expiration
    const checkInterval = setInterval(checkSessionExpiration, INACTIVITY_CHECK_INTERVAL);

    // Initialize last activity timestamp
    updateLastActivity();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity, true);
      });
      clearInterval(checkInterval);
      if (throttleTimer) clearTimeout(throttleTimer);
    };
  }, [user, checkSessionExpiration]);
  
  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check if user is already logged in
        if (isAuthenticated()) {
          // Get user from local storage
          const currentUser = getCurrentUser();
          
          // Check if session has expired during absence (browser closed/reopened)
          const lastActivity = localStorage.getItem('lastActivity');
          if (lastActivity) {
            const lastActivityTime = parseInt(lastActivity, 10);
            const now = Date.now();
            const timeSinceLastActivity = now - lastActivityTime;
            
            if (timeSinceLastActivity > SESSION_TIMEOUT) {
              // Session expired while user was away - silently logout without modal
              console.log('⏰ Session expired during absence - silent auto-logout');
              removeCurrentUser();
              setUser(null);
              setTrialStatus(null);
              localStorage.removeItem('lastActivity');
              // Don't show modal or navigate - just clear the session
              setLoading(false);
              return; // Don't set user or continue initialization
            }
          }
          
          setUser(currentUser);
          
          // Optionally refresh the user profile from server
          refreshUserProfile();
        }
      } catch (err) {
        console.error('Error initializing auth state:', err);
        setError('Failed to restore authentication state');
      } finally {
        setLoading(false);
      }
    };
    
    initializeAuth();
  }, [handleSessionExpiration]);
  
  // Login function with trial status handling
  const login = (userData, token, trialStatusData = null) => {
    setCurrentUser(userData, token);
    setUser(userData);
    
    // Initialize session activity timestamp
    updateLastActivity();
    
    // Handle trial status if provided
    if (trialStatusData) {
      setTrialStatus(trialStatusData);
      
      // Show modal if trial is expired
      if (trialStatusData.expired) {
        setShowTrialExpiredModal(true);
        // Don't navigate if trial is expired - user needs to handle payment first
        return;
      }
    }
    
    // Members go straight to Tasks; owners go to Connect Calendar
    navigate(userData.role === 'member' || userData.role === 'admin' ? '/tasks' : '/register');
  };
  
  // Logout function
  const logout = () => {
    removeCurrentUser();
    setUser(null);
    setImpersonating(null);
    setTrialStatus(null);
    setShowTrialExpiredModal(false);
    setShowPaymentMethodRequiredModal(false);
    setShowSessionExpiredModal(false);
    localStorage.removeItem('lastActivity');
    navigate('/');
  };

  const startImpersonation = (targetUser) => {
    setImpersonating({ actualUser: user, impersonatedUser: targetUser });
  };

  const endImpersonation = () => {
    setImpersonating(null);
  };
  
  // Refresh user profile from server
  const refreshUserProfile = async () => {
    try {
      setLoading(true);
      const result = await getUserProfile();
      
      if (result.success) {
        // Update user data in state and localStorage
        setUser(result.user);
        
        // Keep the existing token, just update the user data
        const token = localStorage.getItem('token');
        setCurrentUser(result.user, token);
        
        // Also refresh trial status if user data has trial info
        if (result.user) {
          const newTrialStatus = {
            expired: result.user.is_trial === 'true' && result.user.trial_status === 'expired',
            active: result.user.is_trial === 'true' && result.user.trial_status === 'active',
            days_remaining: result.user.trial_days_remaining || 0
          };
          setTrialStatus(newTrialStatus);
          
          // Hide trial expired modal if user is no longer in expired trial state
          if (!newTrialStatus.expired) {
            setShowTrialExpiredModal(false);
          }
        }
        
        return true;
      } else {
        setError(result.message);
        return false;
      }
    } catch (err) {
      console.error('Error refreshing user profile:', err);
      setError('Failed to refresh user profile');
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  // Update user profile
  const updateUserProfile = (updatedUserData) => {
    const token = localStorage.getItem('token');
    setCurrentUser(updatedUserData, token);
    setUser(updatedUserData);
  };
  
  // Trial-related functions
  const dismissTrialExpiredModal = () => {
    setShowTrialExpiredModal(false);
  };
  
  const handleTrialExpired = () => {
    setShowTrialExpiredModal(false);
    navigate('/billing'); // Redirect to billing page for payment
  };
  
  const checkTrialStatus = () => {
    return trialStatus;
  };
  
  const isTrialExpired = () => {
    return trialStatus?.expired || false;
  };

  const handleBillingClose = (onClose) => {
    if (isTrialExpired()) {
      // If trial is expired, redirect back to trial expired modal
      setShowTrialExpiredModal(true);
    } else {
      // If trial is not expired, proceed with normal close
      if (onClose) {
        onClose();
      } else {
        navigate('/register');
      }
    }
  };

  // Payment method-related functions
  const dismissPaymentMethodRequiredModal = () => {
    setShowPaymentMethodRequiredModal(false);
  };
  
  const handlePaymentMethodRequired = () => {
    setShowPaymentMethodRequiredModal(false);
    navigate('/billing', { state: { activeTab: 'payment' } }); // Redirect to billing page payment tab
  };
  
  const showPaymentMethodModal = () => {
    setShowPaymentMethodRequiredModal(true);
  };

  const checkPaymentMethodRequired = (user, paymentMethods = []) => {
    // Only check payment method for paid users (not trial users)
    if (user && user.accountType === 'paid' && (!paymentMethods || paymentMethods.length === 0)) {
      setShowPaymentMethodRequiredModal(true);
      return true;
    }
    return false;
  };

  // Function to fetch billing data and check payment method requirement
  const checkAndEnforcePaymentMethod = async () => {
    if (!user || !user.id) return false;
    
    try {
      // Only check for paid account types (not trial)
      const isTrialUser = user?.accountType?.toLowerCase() === 'trial' || 
                          user?.is_trial === 'true' || 
                          user?.trial_status === 'active';
      
      // Skip check for trial users
      if (isTrialUser) {
        return false;
      }
      
      // Fetch billing data to get payment methods
      const response = await apiClient.get(`/api/billing/${user.id}`);
      
      if (response.data && response.data.success && response.data.data) {
        const billingInfo = response.data.data;
        const paymentMethods = billingInfo.payment_methods || [];
        
        // Check if paid user has no payment methods
        if (paymentMethods.length === 0) {
          setShowPaymentMethodRequiredModal(true);
          return true;
        }
      }
      
      return false;
    } catch (err) {
      console.error('Error checking payment methods:', err);
      return false;
    }
  };
  
  // Impersonation overrides the effective user for all permission/UI checks
  const effectiveUser = impersonating ? impersonating.impersonatedUser : user;

  // Role helpers — backward-compatible: users without a role field are treated as owners
  const isOwner = !effectiveUser || effectiveUser.role === 'owner' || !effectiveUser.role;
  const isMember = !!effectiveUser && effectiveUser.role === 'member';
  const isAdmin = !!effectiveUser && effectiveUser.role === 'admin';
  const canManageUsersTeams = isOwner || !!(effectiveUser?.admin_permissions?.manage_users);
  const canManageBilling = isOwner || !!(effectiveUser?.admin_permissions?.manage_billing);

  // Independent delegated-admin grant: lets a user keep their own role (e.g. 'owner'
  // of their own separate paid workspace, or 'member'/'lead_agent' on a team) while
  // ALSO administering a DIFFERENT Account Owner's workspace as a delegated Admin
  // there — stored separately via admin_for_owner_* fields so it never overwrites
  // or is conflated with the user's own role/workspace. Deliberately NOT folded into
  // isAdmin/isOwner (which describe the user's own account type everywhere else in
  // the app) — this flag exists only for the specific places that need to resolve
  // access to the delegated workspace (e.g. Users.js's workspace view).
  const hasDelegatedWorkspace = !!(effectiveUser?.admin_for_owner_email && effectiveUser?.admin_for_owner_user_id);
  const canManageDelegatedWorkspace = hasDelegatedWorkspace && !!effectiveUser?.admin_for_owner_permissions?.manage_users;

  // Value to be provided by context
  const value = {
    user: effectiveUser,
    actualUser: impersonating?.actualUser ?? null,
    isImpersonating: !!impersonating,
    impersonatedUser: impersonating?.impersonatedUser ?? null,
    startImpersonation,
    endImpersonation,
    loading,
    error,
    trialStatus,
    showTrialExpiredModal,
    showPaymentMethodRequiredModal,
    showSessionExpiredModal,
    setShowSessionExpiredModal,
    showSignInModal,
    setShowSignInModal,
    isAuthenticated: !!user,
    isOwner,
    isMember,
    isAdmin,
    canManageUsersTeams,
    canManageBilling,
    hasDelegatedWorkspace,
    canManageDelegatedWorkspace,
    login,
    logout,
    refreshUserProfile,
    updateUserProfile,
    dismissTrialExpiredModal,
    handleTrialExpired,
    checkTrialStatus,
    isTrialExpired,
    handleBillingClose,
    dismissPaymentMethodRequiredModal,
    handlePaymentMethodRequired,
    showPaymentMethodModal,
    checkPaymentMethodRequired,
    checkAndEnforcePaymentMethod
  };
  
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
